/**
 * The backend conversational agent, as seen from the widget.
 *
 * This is the second, deliberately separate answering path. The deterministic
 * skills (and the model *planner* that selects among them) can only answer
 * questions some hand-written skill already covers; they return a verified,
 * reproducible number. The agent answers anything the allow-listed, read-only
 * API can support, by letting the backend model compose from live rows.
 *
 * The two are not interchangeable, and the ordering between them is the whole
 * point: a matching skill always wins, because its answer is deterministic and
 * has been reviewed. The agent is the fallback for the long tail of questions no
 * skill covers — the "why was I charged" shape — where a model-composed answer,
 * for all its uncertainty, beats "I cannot answer that yet".
 *
 * Security lives entirely on the backend: every operation the agent can call is
 * dispatched with the caller's own token and is on an explicit allow-list, so
 * this client can hand it arbitrary text without widening what the signed-in
 * user could already reach. Nothing here constructs a request or names an
 * endpoint.
 */

import request from '../../util/SuperagentUtils';
import { assistantEndpoints } from '../../requests/api-endpoints';
import { getErrorMessage } from '../../util/NetworkErrorUtil';

/** A composed answer plus the operations that produced it, for auditing. */
export interface AgentReply {
  text: string;
  /** operationIds the backend called while answering; empty when it used none. */
  toolsUsed: string[];
}

export interface AssistantAgent {
  /**
   * Answers free text, or resolves to `null` when the agent is disabled or
   * cannot be reached — the caller then keeps its existing "I cannot answer
   * that yet" reply. A `null` is "no answer available", never an error to
   * surface: an agent outage should read as the assistant being less capable,
   * not broken.
   */
  ask: (message: string) => Promise<AgentReply | null>;
}

/**
 * A turn sits in front of a person waiting, and the agent may make several
 * tool calls before it replies, so the ceiling is generous compared with the
 * planner's — but still bounded, so a stuck backend turns into the ordinary
 * decline rather than a spinner that never resolves.
 */
const ACT_TIMEOUT_MS = 45000;

interface ActResponseBody {
  message?: string | null;
  toolsUsed?: string[] | null;
}

/**
 * The browser's IANA time zone, which the backend uses to tell the model what
 * "today" is for this user. Left out when the browser cannot say; the backend
 * then takes today in UTC.
 */
const browserTimeZone = (): string | undefined => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === 'string' && zone.length > 0 ? zone : undefined;
  } catch {
    return undefined;
  }
};

/** The agent that is used when the feature is off: never reaches the network. */
const disabledAgent: AssistantAgent = {
  ask: async () => null,
};

export interface CreateAgentOptions {
  /**
   * Whether the widget may fall back to the backend agent. Off by default: the
   * agent costs a model call and must be enabled deliberately, per audience,
   * once the backend allow-list is proven for that account type.
   */
  enabled?: boolean;
}

export const createAgent = ({ enabled = false }: CreateAgentOptions = {}): AssistantAgent => {
  if (!enabled) return disabledAgent;

  return {
    ask: async (message: string): Promise<AgentReply | null> => {
      try {
        const timeZone = browserTimeZone();
        const response = await request
          .post(assistantEndpoints.act)
          .timeout(ACT_TIMEOUT_MS)
          .send(timeZone ? { message, timeZone } : { message });

        const body = response?.body as ActResponseBody | undefined;
        const text = body?.message;
        // An empty body is treated as no answer rather than an empty bubble:
        // the backend always sends prose when it succeeds, even to decline.
        if (typeof text !== 'string' || text.trim().length === 0) return null;

        return {
          text,
          toolsUsed: Array.isArray(body?.toolsUsed) ? body!.toolsUsed! : [],
        };
      } catch (err) {
        // Swallowed on purpose. A failed agent call must degrade to the local
        // decline, never surface as an error on a question the user may not
        // have expected an answer to anyway. Logged so a persistent outage is
        // visible in development rather than silent.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Assistant agent call failed:', getErrorMessage(err));
        }
        return null;
      }
    },
  };
};

export default createAgent;
